/**
 * pacing.js - reading the engine's recommendation, once.
 *
 * PURE. PLATFORM SCAFFOLD.
 *
 * ---------------------------------------------------------------------------
 * THE ENGINE DECIDES PACING, NOT THE GAME
 *
 * Raise the support, fade it, offer the next level, step back - all of it is
 * `progression.evaluateProgression()`, and a game that invents its own rule
 * makes a Green box mean something different in its own corner of the chart
 * (section 9.1). What a game is left with is OBEYING, and obeying has exactly
 * three shapes:
 *
 *   support moves     automatically, in both directions, never offered. The
 *                     prompt level is not something a child is asked about.
 *   an offer stands   `advance_offer` and `suggest_previous_level` are OFFERS.
 *                     They are held until the level ends and presented there;
 *                     never taken on the child's behalf mid-level.
 *   the game is over  mastered with nowhere to go. At the last level there is
 *                     no next level, so the engine returns `continue` - correct,
 *                     and invisible to anything watching only for an advance.
 *                     `mastered` with a null target is the signal
 *                     (GAME-02-CONTRACT.md section 1.4).
 *
 * This was the third thing written three times. It is one thing.
 */

import { ACTIONS } from '../../../engine/progression.js';
import { applyRecommendation } from './support.js';

/**
 * @typedef {object} PacingRead
 * @property {{index:number, changed:boolean, direction:'up'|'down'|null,
 *             reason:string|null, raise:boolean}|null} support
 *           where the rung goes now, if anywhere. `raise` is section 8.4's
 *           protective increase; the other direction is section 2.5's fade.
 * @property {{kind:'advance'|'back'|'complete', target:object|null,
 *             reason:string|null}|null} offer
 *           what to present when the level ends.
 */

/**
 * @param {object|null} rec  a progression.evaluateProgression() result
 * @param {{phase:number, supportIndex:number}} at
 * @returns {PacingRead}
 */
export function readRecommendation(rec, { phase, supportIndex }) {
  const none = { support: null, offer: null };
  if (!rec) return none;

  if (rec.action === ACTIONS.INCREASE_SUPPORT || rec.action === ACTIONS.REDUCE_SUPPORT) {
    const applied = applyRecommendation(rec, { phase, index: supportIndex });
    return {
      support: { ...applied, raise: rec.action === ACTIONS.INCREASE_SUPPORT },
      offer: null,
    };
  }

  if (rec.action === ACTIONS.ADVANCE_OFFER && rec.target) {
    return { support: null, offer: { kind: 'advance', target: rec.target, reason: rec.reason || null } };
  }

  if (rec.action === ACTIONS.SUGGEST_PREVIOUS_LEVEL && rec.target) {
    return { support: null, offer: { kind: 'back', target: rec.target, reason: rec.reason || null } };
  }

  // Every scoreable box Green and nothing above it in this game's coverage.
  if (rec.mastered && !rec.target) {
    return { support: null, offer: { kind: 'complete', target: null, reason: rec.reason || null } };
  }

  return none;
}

export default { readRecommendation };
